import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { 
  Lightbulb, 
  Settings, 
  Zap, 
  Monitor, 
  Music, 
  Scale, 
  Users, 
  Shield, 
  Target, 
  ExternalLink,
  Mail,
  AlertTriangle,
  Heart
} from 'lucide-react';

const DisplayGuide = () => {
  return (
    <>
      <Helmet>
        <title>Display Owner Guide - Light Hunt</title>
        <meta name="description" content="Complete guide for holiday light display owners. Learn about equipment, software, legal considerations, and best practices for creating amazing synchronized light shows." />
        <meta name="keywords" content="light display guide, Christmas lights setup, synchronized lights, xLights, Light-O-Rama, holiday display equipment, pixel controllers, music licensing" />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <NavBar />
        <main className="grow container mx-auto px-4 py-8 max-w-4xl">
          <div className="space-y-16">
            {/* Hero Section */}
            <div className="text-center space-y-6">
              <h1 className="text-4xl md:text-5xl font-bold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                Display Owner Guide
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Everything you need to create a show-stopping light display
              </p>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Putting together a show-stopping light display takes creativity, planning, and the right gear. Whether you're just getting started or have years of experience, this guide covers the essential equipment, software, and best practices for a safe and successful display.
              </p>
            </div>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Equipment & Software Section */}
            <section className="space-y-8">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <Settings className="h-8 w-8 text-primary" />
                  <h2 className="text-3xl font-bold">Recommended Equipment & Software</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  To create a synchronized light show, you'll need the following:
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {/* Lights */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-4">
                    <Lightbulb className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-semibold">Lights</h3>
                  </div>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span><strong>Smart Pixels:</strong> WS2811 or WS2812 programmable LED lights are the most popular choice for custom sequencing.</span>
                    </li>
                  </ul>
                </div>

                {/* Controllers */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-4">
                    <Monitor className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-semibold">Controllers</h3>
                  </div>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Falcon F16V3 or similar pixel controllers</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Light‑O‑Rama AC controllers (great for traditional lights)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Any controller compatible with xLights or Light‑O‑Rama</span>
                    </li>
                  </ul>
                </div>

                {/* Power & Connections */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-4">
                    <Zap className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-semibold">Power & Connections</h3>
                  </div>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Quality power supplies (calculate wattage based on pixel count)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Extension cords and Cat‑5/ethernet cable for data distribution</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Weatherproof enclosures to protect hardware from the elements</span>
                    </li>
                  </ul>
                </div>

                {/* Software */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-4">
                    <Music className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-semibold">Software</h3>
                  </div>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span><strong>xLights:</strong> Free, open-source sequencing software used by most pixel-based displays</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span><strong>Light‑O‑Rama:</strong> All-in-one system, ideal for those using their ecosystem</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Partnership CTA */}
              <div className="bg-primary/5 border border-primary/10 rounded-lg p-6 text-center">
                <p className="text-muted-foreground">
                  Want to team up, showcase your products, or offer services to display owners?{' '}
                  <Button asChild variant="link" className="px-0 text-primary">
                    <Link to="/contact">Contact us here</Link>
                  </Button>
                  {' '}for partnership opportunities.
                </p>
              </div>
            </section>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Legal & Public Display Considerations */}
            <section className="space-y-8">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <Scale className="h-8 w-8 text-primary" />
                  <h2 className="text-3xl font-bold">Legal & Public Display Considerations</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Creating a public or neighborhood-facing light show comes with responsibility. Here are key areas to keep in mind:
                </p>
              </div>

              <div className="grid gap-6">
                {/* Music Licensing */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-4">
                    <Music className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-semibold">Music Licensing</h3>
                  </div>
                  <p className="text-muted-foreground mb-3">
                    If your display plays music that others can hear (via speakers or FM broadcast), be aware of:
                  </p>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Copyright and performance licenses (e.g., ASCAP, BMI, SESAC)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>For residential displays, many operators rely on fair use or limit volume to avoid issues—but know the risks</span>
                    </li>
                  </ul>
                </div>

                {/* Public Safety & Community */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-3 mb-4">
                    <Users className="h-6 w-6 text-primary" />
                    <h3 className="text-xl font-semibold">Public Safety & Community</h3>
                  </div>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span><strong>HOA or neighborhood rules:</strong> Always check before starting</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Be mindful of noise levels, traffic congestion, and light pollution</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-primary mt-1">•</span>
                      <span>Use signage and clear walkways if you invite viewers on foot</span>
                    </li>
                  </ul>
                </div>

                {/* Laser Safety */}
                <div className="p-6 rounded-lg border bg-card hover:shadow-md transition-shadow">
                  <div className="flex items-start space-x-3 mb-4">
                    <AlertTriangle className="h-6 w-6 text-amber-500" />
                    <h3 className="text-xl font-semibold">Laser Safety (if applicable)</h3>
                  </div>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start space-x-2">
                      <span className="text-amber-500 mt-1">•</span>
                      <span>FDA/FAA variance required for high-powered lasers used outdoors</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-amber-500 mt-1">•</span>
                      <span>No audience scanning allowed—never aim lasers at viewers or above horizon</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Divider */}
            <div className="flex justify-center">
              <div className="w-16 h-px bg-linear-to-r from-transparent via-border to-transparent"></div>
            </div>

            {/* Closing Statement */}
            <section className="text-center space-y-6">
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                At Light Hunt, we're all about making it easier to find, share, and celebrate synchronized light displays. By staying informed and supporting one another, we can build a brighter, safer, and more welcoming community for all.
              </p>
            </section>

            {/* Call to Action */}
            <section className="text-center space-y-6 bg-primary/5 rounded-2xl p-8 md:p-12 border border-primary/10">
              <h2 className="text-3xl font-bold flex items-center justify-center space-x-2">
                <Heart className="h-8 w-8 text-primary" />
                <span>Ready to shine?</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Start uploading your sequences and connect with other display owners today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8">
                <Button asChild size="lg" className="px-8">
                  <Link to="/upload-sequence">
                    Upload Your Sequences
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="px-8">
                  <Link to="/displays">
                    Browse Displays
                  </Link>
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-6">
                Join the growing Light Hunt community and make this season brighter for everyone.
              </p>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default DisplayGuide; 